import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { FieldLabel, RadioCardGroup, Textarea, TextField } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import AssistantIdentityFields from '~/components/pages/ai/digital-assistants/AssistantIdentityFields';
import AgentPickerModal from '~/components/pages/ai/digital-assistants/AgentPickerModal';
import AssistantVersionHistory from '~/components/pages/ai/digital-assistants/AssistantVersionHistory';
import AssistantVersionPreviewModal from '~/components/pages/ai/digital-assistants/AssistantVersionPreviewModal';
import SchemaBuilder from '~/components/pages/ai/digital-assistants/schema/SchemaBuilder';
import SchemaImportModal from '~/components/pages/ai/digital-assistants/schema/SchemaImportModal';
import SchemaJsonPreviewModal from '~/components/pages/ai/digital-assistants/schema/SchemaJsonPreviewModal';
import AssistantTester from '~/components/pages/ai/digital-assistants/tester/AssistantTester';
import ModelSelect from '~/components/pages/ai/common/ModelSelect';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSection,
  ConfirmModal,
  FormActions,
  SkeletonFields,
  Tabs,
  LoadError,
  TagsEditor,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useTransientParam } from '~/hooks/useTransientParam';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { useUrlParams } from '~/hooks/useUrlParams';
import { apiErrorMessage, errorText } from '~/lib/apiError';
import { DIGITAL_ASSISTANT_STATUS_OPTIONS } from '~/lib/ai/digitalAssistant';
import {
  isCanonicalJsonSchema,
  jsonSchemaToBusinessFieldsPreview,
  jsonSchemaToSchemaFields,
  schemaFieldsToJsonSchema,
} from '~/lib/ai/jsonSchema';
import { buildActiveProviderModelOptions } from '~/lib/ai/modelOptions';
import type { SchemaField } from '~/lib/ai/schema';
import { canonicalSchemaToTesterSchema } from '~/lib/ai/schemaForm';
import {
  useArchiveDigitalAssistantVersionMutation,
  useDeleteDigitalAssistantMutation,
  useExecuteDigitalAssistantMutation,
  useGetDigitalAssistantQuery,
  useGetDigitalAssistantVersionQuery,
  useRestoreDigitalAssistantVersionMutation,
  useUpdateDigitalAssistantMutation,
} from '~/modules/query/ai/digitalAssistants';
import {
  useGetAgentsQuery,
  useGetFieldCatalogQuery,
  useGetProvidersQuery,
} from '~/modules/query/ai/providers';
import type {
  DigitalAssistant,
  DigitalAssistantVersion,
  FieldCatalogEntry,
} from '~/modules/query/ai/types';
import { routes } from '~/app/routes';

const TAB_CONFIG = 'config';
const TAB_HISTORY = 'history';
const MAX_DESCRIPTION_LENGTH = 160;

type AssistantStatus = 'draft' | 'published';

interface AssistantDraft {
  agentId: string;
  description: string;
  fields: SchemaField[];
  legacySchema: boolean;
  model: string;
  name: string;
  prompt: string;
  status: AssistantStatus;
  unmappableKeys: string[];
}

const STATUS_DESCRIPTIONS: Record<AssistantStatus, string> = {
  published: 'Placements and the runtime use this configuration.',
  draft: 'Only the draft is saved. Testing keeps using the last published version.',
};

const STATUS_OPTIONS = DIGITAL_ASSISTANT_STATUS_OPTIONS.map((option) => ({
  ...option,
  description: STATUS_DESCRIPTIONS[option.value],
}));

const FORM_ID = 'digital-assistant-form';

/** The schema builder's own problem is a message, not a field; an empty one is no problem. */
const assistantSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name.'),
  agentId: z.string().min(1, 'Choose an agent.'),
  model: z.string().trim().min(1, 'Choose an AI model.'),
  prompt: z.string().trim().min(1, 'Enter a prompt.'),
  schemaProblem: z.string().superRefine((problem, ctx) => {
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  }),
});

const FIELD_IDS: Record<string, string> = {
  name: 'digital-assistant-name',
  agentId: 'digital-assistant-agent',
  model: 'digital-assistant-model',
  prompt: 'digital-assistant-prompt',
  schemaProblem: 'digital-assistant-schema-error',
};

const TABS = [
  { id: TAB_CONFIG, label: 'Configuration' },
  { id: TAB_HISTORY, label: 'Version history' },
];

function draftFromAssistant(
  assistant: DigitalAssistant,
  catalog: FieldCatalogEntry[],
): AssistantDraft {
  const { fields, unmappableKeys } = jsonSchemaToSchemaFields(
    assistant.draftInputPayloadSchema,
    catalog,
  );

  return {
    agentId: assistant.agentId || '',
    description: assistant.description ?? '',
    fields,
    legacySchema:
      assistant.draftInputPayloadSchema != null &&
      !isCanonicalJsonSchema(assistant.draftInputPayloadSchema),
    model: assistant.draftModel || '',
    name: assistant.name,
    prompt: assistant.draftPromptTemplate || '',
    status: assistant.status === 'published' ? 'published' : 'draft',
    unmappableKeys,
  };
}

const readVersionParam = (raw: string | null): number | null => {
  const parsed = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/** One assistant: the draft form, the schema builder, the published-version tester and the history. */
export default function DigitalAssistantDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const url = useUrlParams();
  const navigate = useNavigate();

  const assistantQuery = useGetDigitalAssistantQuery(id, { skip: !id });
  const agentsQuery = useGetAgentsQuery();
  const catalogQuery = useGetFieldCatalogQuery();
  const providersQuery = useGetProvidersQuery();

  const [updateAssistant] = useUpdateDigitalAssistantMutation();
  const [deleteAssistant] = useDeleteDigitalAssistantMutation();
  const [restoreVersion] = useRestoreDigitalAssistantVersionMutation();
  const [archiveVersion] = useArchiveDigitalAssistantVersionMutation();
  const [executeAssistant] = useExecuteDigitalAssistantMutation();

  const assistant = assistantQuery.currentData?.data;
  const catalog = useMemo(() => catalogQuery.data?.fields ?? [], [catalogQuery.data]);
  const agents = useMemo(() => agentsQuery.data?.data ?? [], [agentsQuery.data]);

  // The form waits for the agent names and the catalog: the Agent field and the schema's reverse
  // mapping both read them, and an early seed would show raw ids. A failed catalog would seed every
  // stored field as unmappable and count as an empty schema, so the form never opens on one.
  const ready =
    !(assistant?.agentId && agentsQuery.isLoading) &&
    !catalogQuery.isLoading &&
    !catalogQuery.isError;
  const { draft, patch, dirty, markSaved } = useRecordDraft(ready ? assistant : undefined, {
    id,
    version: assistant?.updatedAt,
    recordId: (a) => a.id,
    toDraft: (record) => draftFromAssistant(record, catalog),
  });
  const guard = useUnsavedGuard(dirty);

  const [actionError, setActionError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [schemaModal, setSchemaModal] = useState<'import' | 'preview' | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Tags are on-screen only (nothing saves them); a different assistant starts empty.
  const [tags, setTags] = useState<string[]>([]);
  const [tagsFor, setTagsFor] = useState(id);
  if (tagsFor !== id) {
    setTagsFor(id);
    setTags([]);
  }
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<number | null>(null);

  const agentName = agents.find((agent) => agent.id === draft?.agentId)?.name ?? '';

  const { invalidPropertyNames, schema: generatedSchema } = useMemo(
    () => schemaFieldsToJsonSchema(draft?.fields ?? [], catalog, draft?.name ?? ''),
    [draft?.fields, catalog, draft?.name],
  );
  const schemaProblem = catalogQuery.isLoading
    ? 'The business field catalog is still loading. Try again in a moment.'
    : catalogQuery.isError
      ? 'The business field catalog could not be loaded. Reload it before saving.'
      : invalidPropertyNames.length > 0
        ? 'Remove the fields that are no longer in the catalog before saving.'
        : '';
  const check = useFormValidation(
    assistantSchema,
    draft && { ...draft, schemaProblem },
    (field) => FIELD_IDS[field] ?? field,
  );

  const save = useRecordSave<AssistantDraft>({
    save: (d) =>
      updateAssistant({
        id,
        agentId: d.agentId,
        model: d.model.trim(),
        name: d.name.trim(),
        promptTemplate: d.prompt.trim(),
        description: d.description.trim() || null,
        inputPayloadSchema: d.fields.length > 0 ? generatedSchema : null,
        status: d.status,
      }).unwrap(),
    success: 'Digital assistant saved',
    failure: 'The assistant could not be saved. Try again.',
  });

  const publishedVersionNumber = assistant?.publishedVersion ?? null;
  const publishedVersionQuery = useGetDigitalAssistantVersionQuery(
    { id, versionNumber: publishedVersionNumber ?? 0 },
    { skip: !id || publishedVersionNumber === null },
  );
  const testerSchema = useMemo(
    () =>
      canonicalSchemaToTesterSchema(
        publishedVersionQuery.data?.data.inputPayloadSchema,
        publishedVersionQuery.data?.data.name,
      ),
    [publishedVersionQuery.data],
  );

  const assistantKey = assistant?.assistantKey ?? '';
  const runTest = useCallback(
    async (input: Record<string, unknown>, userInput?: string) => {
      const response = await executeAssistant({
        assistantKey,
        input,
        ...(userInput ? { userInput } : {}),
      }).unwrap();
      return response.result;
    },
    [executeAssistant, assistantKey],
  );

  const tab = url.get('tab') === TAB_HISTORY ? TAB_HISTORY : TAB_CONFIG;
  const setTab = (next: string) => url.set({ tab: next === TAB_CONFIG ? null : next });
  const viewer = useTransientParam('version');
  const previewVersionNumber = readVersionParam(viewer.value);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !check.submit()) return;
    setActionError(null);
    if (await save.submit(draft)) markSaved(draft);
  };

  const confirmRestore = () => {
    if (restoreTarget === null) {
      return;
    }
    const versionNumber = restoreTarget;
    setRestoreTarget(null);
    setActionError(null);
    restoreVersion({ id, versionNumber })
      .unwrap()
      .then((response) => {
        if (draft) markSaved(draft, draftFromAssistant(response.data, catalog));
        toast.success(`Version ${versionNumber} restored`);
      })
      .catch((error) =>
        setActionError(errorText(error, 'The version could not be restored. Try again.')),
      );
  };

  const confirmArchive = () => {
    if (archiveTarget === null) {
      return;
    }
    const versionNumber = archiveTarget;
    setArchiveTarget(null);
    setActionError(null);
    archiveVersion({ id, versionNumber })
      .unwrap()
      .then(() => toast.success(`Version ${versionNumber} archived`))
      .catch((error) =>
        setActionError(errorText(error, 'The version could not be archived. Try again.')),
      );
  };

  const confirmDelete = () => {
    setDeleteOpen(false);
    setActionError(null);
    deleteAssistant(id)
      .unwrap()
      .then(() => {
        toast.success('Digital assistant deleted');
        guard.release();
        navigate(routes.aiDigitalAssistants);
      })
      .catch((error) =>
        setActionError(errorText(error, 'The assistant could not be deleted. Try again.')),
      );
  };

  const breadcrumbs = [
    { label: 'AI' },
    { label: 'Digital assistants', href: routes.aiDigitalAssistants },
    { label: assistant?.name ?? 'Digital assistant' },
  ];

  if (!assistant) {
    return (
      <RecordPageShell
        title="Digital assistant"
        breadcrumbs={breadcrumbs}
        loading={!assistantQuery.isError}
        error={
          assistantQuery.isError
            ? {
                what: 'this digital assistant',
                message:
                  apiErrorMessage(assistantQuery.error) ??
                  'It may have been deleted, or the service is unavailable.',
              }
            : null
        }
        onRetry={() => assistantQuery.refetch()}
        testId="ai-digital-assistant"
      />
    );
  }

  // The record arrived but the seed waits for the agent names and the field catalog.
  if (catalogQuery.isError) {
    return (
      <RecordPageShell
        title={assistant.name}
        breadcrumbs={breadcrumbs}
        error={{
          what: 'the business field catalog',
          message:
            apiErrorMessage(catalogQuery.error) ?? 'The input schema cannot be edited without it.',
        }}
        onRetry={() => catalogQuery.refetch()}
        testId="ai-digital-assistant-catalog"
      />
    );
  }

  if (!draft) {
    return <RecordPageShell title={assistant.name} breadcrumbs={breadcrumbs} loading />;
  }

  const activeModelCount = buildActiveProviderModelOptions(providersQuery.data?.models).length;

  return (
    <RecordPageShell
      title={assistant.name}
      breadcrumbs={breadcrumbs}
      actions={[
        {
          label: 'Delete assistant',
          kind: 'danger',
          testId: 'ai-digital-assistants-detail-delete',
          onClick: () => setDeleteOpen(true),
          disabled: save.busy,
        },
      ]}
      guard={guard}
      testId="ai-digital-assistant"
    >
      {actionError ? (
        <Alert variant="error">
          <p className="text-sm text-gray-800 dark:text-white/90">{actionError}</p>
        </Alert>
      ) : null}

      <Tabs
        tabs={TABS}
        activeId={tab}
        onChange={setTab}
        ariaLabel="Digital assistant sections"
        idPrefix="assistant-detail"
      />

      {tab === TAB_CONFIG ? (
        <section
          role="tabpanel"
          id="assistant-detail-panel-config"
          aria-labelledby="assistant-detail-tab-config"
        >
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="xl:col-span-2">
              <form id={FORM_ID} noValidate onSubmit={submit}>
                <Card>
                  <CardHeader
                    title="Configuration"
                    description="What the assistant is, the input it accepts and how it answers."
                  />
                  <CardBody className="space-y-6">
                    {save.error && <Alert variant="error" message={save.error} />}
                    <CardSection title="Details">
                      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        <AssistantIdentityFields
                          isNew={false}
                          name={draft.name}
                          assistantKey={assistant.assistantKey}
                          onNameChange={(name) => patch({ name })}
                          onNameBlur={() => check.touch('name')}
                          nameError={check.errors.name}
                        />
                        <div className="space-y-2 lg:col-span-2">
                          <FieldLabel>Status</FieldLabel>
                          <RadioCardGroup
                            name="digital-assistant-status"
                            ariaLabel="Status"
                            value={draft.status}
                            onChange={(status) => patch({ status })}
                            options={STATUS_OPTIONS}
                          />
                        </div>
                        <div className="lg:col-span-2">
                          <TagsEditor
                            tags={tags}
                            onChange={setTags}
                            label="Tags"
                            tooltip="Tags are not saved yet. They clear when you leave this page."
                            testId="ai-digital-assistants-detail-tags"
                          />
                        </div>
                        <TextField
                          id="digital-assistant-agent"
                          testId="ai-digital-assistants-detail-agent"
                          label="Agent"
                          required
                          readOnly
                          value={agentName || draft.agentId}
                          placeholder="No agent selected"
                          error={check.errors.agentId}
                          helperText={
                            !agentName && draft.agentId
                              ? "The agent's name is unavailable right now, so its saved ID is shown instead. The saved agent stays selected."
                              : undefined
                          }
                          onChange={() => undefined}
                          trailingActions={
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPickerOpen(true)}
                              testId="ai-digital-assistants-detail-browse-agent"
                            >
                              Browse
                            </Button>
                          }
                        />
                        <ModelSelect
                          id="digital-assistant-model"
                          testId="ai-digital-assistants-detail-model"
                          label="AI model"
                          required
                          value={draft.model}
                          models={providersQuery.data?.models}
                          error={
                            check.errors.model ??
                            (providersQuery.isError
                              ? 'The model list could not be loaded. The saved model stays selected.'
                              : undefined)
                          }
                          helperText={
                            activeModelCount === 0
                              ? 'No model is active. Activate one in Settings › Models.'
                              : undefined
                          }
                          onChange={(model) => {
                            patch({ model });
                            check.touch('model');
                          }}
                        />
                        <div className="lg:col-span-2">
                          <Textarea
                            id="digital-assistant-description"
                            testId="ai-digital-assistants-detail-description"
                            label="Description"
                            rows={3}
                            maxLength={MAX_DESCRIPTION_LENGTH}
                            value={draft.description}
                            onChange={(description) => patch({ description })}
                            placeholder="What this assistant is for"
                          />
                        </div>
                      </div>
                    </CardSection>

                    <CardSection
                      title="Input schema"
                      description="The structured fields the runtime sends with each request."
                      actions={
                        <>
                          <Button
                            variant="outline"
                            onClick={() => setSchemaModal('import')}
                            disabled={catalogQuery.isLoading || catalogQuery.isError}
                            testId="ai-digital-assistants-schema-import"
                          >
                            Import JSON
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setSchemaModal('preview')}
                            disabled={catalogQuery.isLoading || catalogQuery.isError}
                            testId="ai-digital-assistants-schema-preview"
                          >
                            Preview JSON
                          </Button>
                        </>
                      }
                    >
                      {draft.unmappableKeys.length > 0 ? (
                        <Alert variant="warning">
                          <p className="text-sm text-gray-800 dark:text-white/90">
                            These saved fields are not in the current business field catalog:{' '}
                            {draft.unmappableKeys.join(', ')}. They cannot be represented by the
                            builder and are left out if you save.
                          </p>
                        </Alert>
                      ) : null}
                      {draft.legacySchema ? (
                        <Alert variant="warning">
                          <p className="text-sm text-gray-800 dark:text-white/90">
                            This assistant's saved input schema is not one the builder can
                            represent. Building a new schema here replaces it when you save.
                          </p>
                        </Alert>
                      ) : null}
                      <SchemaBuilder
                        fields={draft.fields}
                        assistantName={draft.name}
                        catalog={catalog}
                        catalogLoading={catalogQuery.isLoading}
                        onChange={(fields) => patch({ fields, legacySchema: false })}
                      />
                      {check.errors.schemaProblem ? (
                        <p
                          id="digital-assistant-schema-error"
                          tabIndex={-1}
                          role="alert"
                          className="mt-1.5 text-xs text-error-600 dark:text-error-400"
                        >
                          {check.errors.schemaProblem}
                        </p>
                      ) : null}

                      {schemaModal === 'preview' ? (
                        <SchemaJsonPreviewModal
                          json={JSON.stringify(
                            jsonSchemaToBusinessFieldsPreview(generatedSchema, catalog),
                            null,
                            2,
                          )}
                          onClose={() => setSchemaModal(null)}
                        />
                      ) : null}
                      {schemaModal === 'import' ? (
                        <SchemaImportModal
                          catalog={catalog}
                          currentFieldCount={draft.fields.length}
                          onClose={() => setSchemaModal(null)}
                          onImport={(fields) => {
                            patch({ fields, legacySchema: false });
                            setSchemaModal(null);
                          }}
                        />
                      ) : null}
                    </CardSection>

                    <CardSection
                      title="Prompt and instructions"
                      description="How the assistant should answer."
                    >
                      <Textarea
                        id="digital-assistant-prompt"
                        testId="ai-digital-assistants-detail-prompt"
                        label="Prompt"
                        required
                        rows={10}
                        value={draft.prompt}
                        error={check.errors.prompt}
                        onChange={(prompt) => patch({ prompt })}
                        onBlur={() => check.touch('prompt')}
                      />
                    </CardSection>
                  </CardBody>
                  <CardFooter>
                    <FormActions
                      form={FORM_ID}
                      discardHref={routes.aiDigitalAssistants}
                      busy={save.busy}
                      disabled={!dirty}
                      disabledReason="No changes to save"
                      saveTestId="ai-digital-assistants-detail-save"
                      discardTestId="ai-digital-assistants-detail-discard"
                    />
                  </CardFooter>
                </Card>
              </form>
            </div>

            <div>
              <Card>
                <CardHeader
                  title="Test assistant"
                  description="Send sample input to the published configuration."
                />
                <CardBody>
                  {publishedVersionNumber === null ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      This assistant has not been published yet. Set its status to Active and save
                      to enable testing.
                    </p>
                  ) : publishedVersionQuery.isFetching && !publishedVersionQuery.data ? (
                    <SkeletonFields fields={2} />
                  ) : publishedVersionQuery.error ? (
                    <LoadError
                      what="the published configuration"
                      message={apiErrorMessage(publishedVersionQuery.error)}
                      onRetry={() => publishedVersionQuery.refetch()}
                      testId="ai-digital-assistants-published"
                    />
                  ) : (
                    <AssistantTester schema={testerSchema} onTest={runTest} />
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </section>
      ) : (
        <section
          role="tabpanel"
          id="assistant-detail-panel-history"
          aria-labelledby="assistant-detail-tab-history"
        >
          <Card>
            <CardHeader
              title="Version history"
              description="Every save records a version. Restoring one creates a new version with its values."
            />
            <CardBody>
              <AssistantVersionHistory
                assistantId={id}
                latestVersion={assistant.latestVersion}
                onPreview={(version: DigitalAssistantVersion) =>
                  viewer.open(String(version.versionNumber))
                }
                onRestore={(version: DigitalAssistantVersion) =>
                  setRestoreTarget(version.versionNumber)
                }
                onArchive={(version: DigitalAssistantVersion) =>
                  setArchiveTarget(version.versionNumber)
                }
              />
            </CardBody>
          </Card>
        </section>
      )}

      {pickerOpen ? (
        <AgentPickerModal
          agents={agents}
          selectedAgentId={draft.agentId}
          isLoading={agentsQuery.isLoading}
          errorMessage={
            agentsQuery.isError
              ? (apiErrorMessage(agentsQuery.error) ??
                'The agent list could not be loaded. Try again.')
              : undefined
          }
          onRetry={() => agentsQuery.refetch()}
          onClose={() => setPickerOpen(false)}
          onSelect={(agent) => {
            patch({ agentId: agent.id });
            check.touch('agentId');
            setPickerOpen(false);
          }}
        />
      ) : null}

      <AssistantVersionPreviewModal
        assistantId={id}
        versionNumber={previewVersionNumber}
        onClose={viewer.close}
        onRestore={(versionNumber) => {
          viewer.close();
          setRestoreTarget(versionNumber);
        }}
      />

      <ConfirmModal
        isOpen={deleteOpen}
        title="Delete digital assistant"
        message={`Delete ${assistant.name}?`}
        confirmText={assistant.name}
        confirmLabel="Delete assistant"
        variant="danger"
        dangerNote="The assistant and its configuration are removed, and anything that calls it stops working. This cannot be undone."
        testId="ai-digital-assistants-detail-delete-confirm"
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
      />

      <ConfirmModal
        isOpen={restoreTarget !== null}
        title="Restore version"
        message={`Restore version ${restoreTarget}? This creates a new version with that version's values and replaces the draft on this page.`}
        confirmLabel="Restore version"
        testId="ai-digital-assistants-restore-confirm"
        onClose={() => setRestoreTarget(null)}
        onConfirm={confirmRestore}
      />

      <ConfirmModal
        isOpen={archiveTarget !== null}
        title="Archive version"
        message={`Archive version ${archiveTarget}? It is removed from the version history.`}
        confirmLabel="Archive version"
        variant="danger"
        dangerNote="Archived versions cannot be restored from here."
        testId="ai-digital-assistants-archive-confirm"
        onClose={() => setArchiveTarget(null)}
        onConfirm={confirmArchive}
      />
    </RecordPageShell>
  );
}
